import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ScrollToTop } from './components/shared/ScrollToTop'
import { PageLoader, FullPageLoader } from './components/PageLoader'
import { ProtectedRoute } from './components/shared/ProtectedRoute'
import { DashboardLayout } from './layouts/DashboardLayout'
import { PublicLayout } from './layouts/PublicLayout'
import { features } from '@/shared/config/features'

// ============================================
// Eager Loaded Pages (always included in main bundle)
// ============================================

// Auth pages - needed for initial sign-in flow
import { SignInPage } from './modules/auth/SignInPage'
import { SignUpPage } from './modules/auth/SignUpPage'
import { ForgotPasswordPage } from './modules/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './modules/auth/ResetPasswordPage'

// Dashboard - most common landing page after auth
import { DashboardPage } from './pages/DashboardPage'

// Landing page - entry point for non-auth users
import { LandingPage } from './pages/LandingPage'

// ============================================
// Lazy Loaded Pages (loaded on demand)
// ============================================

// Public marketing pages
const FeaturesPage = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })))
const TechnologyPage = lazy(() => import('./pages/TechnologyPage').then(m => ({ default: m.TechnologyPage })))
const ServicesPage = lazy(() => import('./pages/ServicesPage').then(m => ({ default: m.ServicesPage })))
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })))
const GetAQuotePage = lazy(() => import('./pages/GetAQuotePage').then(m => ({ default: m.GetAQuotePage })))

// CRM Modules - Contacts
const ContactsPage = lazy(() => import('./modules/contacts/pages/ContactsPage').then(m => ({ default: m.ContactsPage })))
const ContactDetailPage = lazy(() => import('./modules/contacts/pages/ContactDetailPage').then(m => ({ default: m.ContactDetailPage })))

// CRM Modules - Companies
const CompaniesPage = lazy(() => import('./modules/companies/pages/CompaniesPage').then(m => ({ default: m.CompaniesPage })))
const CompanyDetailPage = lazy(() => import('./modules/companies/pages/CompanyDetailPage').then(m => ({ default: m.CompanyDetailPage })))

// CRM Modules - Deals
const DealsPage = lazy(() => import('./modules/deals/pages/DealsPage').then(m => ({ default: m.DealsPage })))
const DealDetailPage = lazy(() => import('./modules/deals/pages/DealDetailPage').then(m => ({ default: m.DealDetailPage })))

// CRM Modules - Quotes
const QuotesPage = lazy(() => import('./modules/quotes/pages/QuotesPage').then(m => ({ default: m.QuotesPage })))
const QuoteDetailPage = lazy(() => import('./modules/quotes/pages/QuoteDetailPage').then(m => ({ default: m.QuoteDetailPage })))
const PublicQuotePage = lazy(() => import('./modules/quotes/pages/PublicQuotePage').then(m => ({ default: m.PublicQuotePage })))

// CRM Modules - Cases
const CasesPage = lazy(() => import('./modules/cases/pages/CasesPage').then(m => ({ default: m.CasesPage })))
const CaseDetailPage = lazy(() => import('./modules/cases/pages/CaseDetailPage').then(m => ({ default: m.CaseDetailPage })))

// CRM Modules - Products
const ProductsPage = lazy(() => import('./modules/products/pages/ProductsPage').then(m => ({ default: m.ProductsPage })))
const ProductDetailPage = lazy(() => import('./modules/products/pages/ProductDetailPage').then(m => ({ default: m.ProductDetailPage })))

// Tasks Module
const TasksPage = lazy(() => import('./modules/tasks/pages/TasksPage').then(m => ({ default: m.TasksPage })))
const TaskDetailPage = lazy(() => import('./modules/tasks/pages/TaskDetailPage').then(m => ({ default: m.TaskDetailPage })))

// Enquiries Module
const EnquiriesPage = lazy(() => import('./modules/enquiries/pages/EnquiriesPage').then(m => ({ default: m.EnquiriesPage })))
const EnquiryDetailPage = lazy(() => import('./modules/enquiries/pages/EnquiryDetailPage').then(m => ({ default: m.EnquiryDetailPage })))

// Docs Module - Large (TipTap + markdown)
const DocsPage = lazy(() => import('./modules/docs/pages/DocsPage').then(m => ({ default: m.DocsPage })))
const DocEditorPage = lazy(() => import('./modules/docs/pages/DocEditorPage').then(m => ({ default: m.DocEditorPage })))
const SpaceEditorPage = lazy(() => import('./modules/docs/pages/SpaceEditorPage').then(m => ({ default: m.SpaceEditorPage })))
const PublicDocsIndexPage = lazy(() => import('./modules/docs/pages/PublicDocsIndexPage').then(m => ({ default: m.PublicDocsIndexPage })))
const PublicDocPage = lazy(() => import('./modules/docs/pages/PublicDocPage').then(m => ({ default: m.PublicDocPage })))

// Calls Module
const CallsPage = lazy(() => import('./modules/calls/pages/CallsPage').then(m => ({ default: m.CallsPage })))
const CallDetailPage = lazy(() => import('./modules/calls/pages/CallDetailPage').then(m => ({ default: m.CallDetailPage })))

// Email Module - Large (TipTap editor)
const EmailPage = lazy(() => import('./modules/email/pages/EmailPage').then(m => ({ default: m.EmailPage })))
const TemplateEditorPage = lazy(() => import('./modules/email/pages/TemplateEditorPage').then(m => ({ default: m.TemplateEditorPage })))
const CampaignEditorPage = lazy(() => import('./modules/email/pages/CampaignEditorPage').then(m => ({ default: m.CampaignEditorPage })))
const UnsubscribePage = lazy(() => import('./modules/email/pages/UnsubscribePage').then(m => ({ default: m.UnsubscribePage })))

// SMS Module - Conversation threading
const SMSConversationsPage = lazy(() => import('./modules/sms/pages/SMSConversationsPage').then(m => ({ default: m.SMSConversationsPage })))

// Media Library Module
const MediaPage = lazy(() => import('./modules/media/pages/MediaPage').then(m => ({ default: m.MediaPage })))

// File Conversion Module
const FileConversionPage = lazy(() => import('./modules/file-conversion/pages/FileConversionPage').then(m => ({ default: m.FileConversionPage })))

// Listings Module
const ListingsPage = lazy(() => import('./modules/listings/pages/ListingsPage').then(m => ({ default: m.ListingsPage })))
const ListingDetailPage = lazy(() => import('./modules/listings/pages/ListingDetailPage').then(m => ({ default: m.ListingDetailPage })))

// Portfolio Module
const PortfolioPage = lazy(() => import('./modules/portfolio/pages/PortfolioPage').then(m => ({ default: m.PortfolioPage })))
const PortfolioItemDetailPage = lazy(() => import('./modules/portfolio/pages/PortfolioItemDetailPage').then(m => ({ default: m.PortfolioItemDetailPage })))
const CollectionDetailPage = lazy(() => import('./modules/portfolio/pages/CollectionDetailPage').then(m => ({ default: m.CollectionDetailPage })))

// Projects Module - Project management with phases and tasks
const ProjectsPage = lazy(() => import('./modules/projects/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })))
const ProjectDetailPage = lazy(() => import('./modules/projects/pages/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage })))

// Discussions Module - Google Spaces-style conversations
const DiscussionsPage = lazy(() => import('./modules/discussions/pages/DiscussionsPage').then(m => ({ default: m.DiscussionsPage })))
const DiscussionSpaceDetailPage = lazy(() => import('./modules/discussions/pages/SpaceDetailPage').then(m => ({ default: m.SpaceDetailPage })))

// Events Module - Meetings, workshops, webinars, conferences
const EventsPage = lazy(() => import('./modules/events/pages/EventsPage').then(m => ({ default: m.EventsPage })))
const EventDetailPage = lazy(() => import('./modules/events/pages/EventDetailPage').then(m => ({ default: m.EventDetailPage })))

// AI Chat Module - General-purpose AI assistant
const AIChatPage = lazy(() => import('./modules/ai-chat/pages/AIChatPage').then(m => ({ default: m.AIChatPage })))
const SharedChatPage = lazy(() => import('./modules/ai-chat/pages/SharedChatPage').then(m => ({ default: m.SharedChatPage })))

// CMS Module - Visual page builder with Puck
const CmsPagesPage = lazy(() => import('./modules/cms/pages/PagesPage').then(m => ({ default: m.PagesPage })))
const CmsPageEditorPage = lazy(() => import('./modules/cms/pages/PageEditorPage').then(m => ({ default: m.PageEditorPage })))
const CmsPublicPage = lazy(() => import('./modules/cms/pages/PublicPage').then(m => ({ default: m.PublicPage })))
const BlogIndexPage = lazy(() => import('./modules/cms/pages/BlogIndexPage').then(m => ({ default: m.BlogIndexPage })))
const BlogCategoryPage = lazy(() => import('./modules/cms/pages/BlogCategoryPage').then(m => ({ default: m.BlogCategoryPage })))
const BlogTagPage = lazy(() => import('./modules/cms/pages/BlogTagPage').then(m => ({ default: m.BlogTagPage })))
const CmsMenusPage = lazy(() => import('./modules/cms/pages/MenusPage').then(m => ({ default: m.MenusPage })))
const CmsMenuDetailPage = lazy(() => import('./modules/cms/pages/MenuDetailPage').then(m => ({ default: m.MenuDetailPage })))
const CmsPageVersionsPage = lazy(() => import('./modules/cms/pages/PageVersionsPage').then(m => ({ default: m.PageVersionsPage })))
const L2EditorTestPage = lazy(() => import('./modules/cms/pages/L2EditorTestPage').then(m => ({ default: m.L2EditorTestPage })))

// Settings Module
const SettingsPage = lazy(() => import('./modules/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))

// Activity Module - Audit trail and activity logging
const ActivityPage = lazy(() => import('./modules/activity/pages/ActivityPage').then(m => ({ default: m.ActivityPage })))

// Reports Module - Analytics and reporting
const ReportsPage = lazy(() => import('./modules/reports/pages/ReportsPage').then(m => ({ default: m.ReportsPage })))
const PipelineReportPage = lazy(() => import('./modules/reports/pages/PipelineReportPage').then(m => ({ default: m.PipelineReportPage })))
const ActivityReportPage = lazy(() => import('./modules/reports/pages/ActivityReportPage').then(m => ({ default: m.ActivityReportPage })))
const LeadsReportPage = lazy(() => import('./modules/reports/pages/LeadsReportPage').then(m => ({ default: m.LeadsReportPage })))
const CasesReportPage = lazy(() => import('./modules/reports/pages/CasesReportPage').then(m => ({ default: m.CasesReportPage })))

// Admin Module - Consolidated settings page
const AdminSettingsPage = lazy(() => import('./modules/admin/pages/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })))
const SystemPromptDetailPage = lazy(() => import('./modules/admin/pages/SystemPromptDetailPage').then(m => ({ default: m.SystemPromptDetailPage })))

// Development Tools
const StyleGuidePage = lazy(() => import('./pages/StyleGuidePage').then(m => ({ default: m.StyleGuidePage })))

// ============================================
// Suspense Wrapper for Lazy Routes
// ============================================

function LazyRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

function LazyFullPageRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageLoader />}>{children}</Suspense>
}

// ============================================
// Application Routes
// ============================================

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
      <Routes>
        {/* Public marketing pages with header/footer */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<LazyRoute><FeaturesPage /></LazyRoute>} />
          <Route path="/technology" element={<LazyRoute><TechnologyPage /></LazyRoute>} />
          <Route path="/services" element={<LazyRoute><ServicesPage /></LazyRoute>} />
        </Route>

        {/* Auth routes (standalone, no layout) */}
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public enquiry form pages */}
        {features.enquiries && (
          <>
            <Route path="/contact" element={<LazyFullPageRoute><ContactPage /></LazyFullPageRoute>} />
            <Route path="/get-a-quote" element={<LazyFullPageRoute><GetAQuotePage /></LazyFullPageRoute>} />
          </>
        )}

        {/* Public email unsubscribe page */}
        <Route path="/unsubscribe/:token" element={<LazyFullPageRoute><UnsubscribePage /></LazyFullPageRoute>} />

        {/* Protected dashboard routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard index page - always available */}
          <Route index element={<DashboardPage />} />

          {/* Core modules - conditionally included based on feature flags */}
          {features.tasks && (
            <>
              <Route path="tasks" element={<LazyRoute><TasksPage /></LazyRoute>} />
              <Route path="tasks/new" element={<LazyRoute><TaskDetailPage /></LazyRoute>} />
              <Route path="tasks/:id" element={<LazyRoute><TaskDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Contacts module with detail pages */}
          {features.contacts && (
            <>
              <Route path="contacts" element={<LazyRoute><ContactsPage /></LazyRoute>} />
              <Route path="contacts/new" element={<LazyRoute><ContactDetailPage /></LazyRoute>} />
              <Route path="contacts/:id" element={<LazyRoute><ContactDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Companies module with detail pages */}
          {features.companies && (
            <>
              <Route path="companies" element={<LazyRoute><CompaniesPage /></LazyRoute>} />
              <Route path="companies/new" element={<LazyRoute><CompanyDetailPage /></LazyRoute>} />
              <Route path="companies/:id" element={<LazyRoute><CompanyDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Deals module with detail pages */}
          {features.deals && (
            <>
              <Route path="deals" element={<LazyRoute><DealsPage /></LazyRoute>} />
              <Route path="deals/new" element={<LazyRoute><DealDetailPage /></LazyRoute>} />
              <Route path="deals/:id" element={<LazyRoute><DealDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Quotes module - customer quotations and estimates */}
          {features.quotes && (
            <>
              <Route path="quotes" element={<LazyRoute><QuotesPage /></LazyRoute>} />
              <Route path="quotes/new" element={<LazyRoute><QuoteDetailPage /></LazyRoute>} />
              <Route path="quotes/:id" element={<LazyRoute><QuoteDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Products module with detail pages */}
          {features.products && (
            <>
              <Route path="products" element={<LazyRoute><ProductsPage /></LazyRoute>} />
              <Route path="products/new" element={<LazyRoute><ProductDetailPage /></LazyRoute>} />
              <Route path="products/:id" element={<LazyRoute><ProductDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Cases module with detail pages */}
          {features.cases && (
            <>
              <Route path="cases" element={<LazyRoute><CasesPage /></LazyRoute>} />
              <Route path="cases/new" element={<LazyRoute><CaseDetailPage /></LazyRoute>} />
              <Route path="cases/:id" element={<LazyRoute><CaseDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Enquiries module */}
          {features.enquiries && (
            <>
              <Route path="enquiries" element={<LazyRoute><EnquiriesPage /></LazyRoute>} />
              <Route path="enquiries/:id" element={<LazyRoute><EnquiryDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Docs & Wiki module */}
          {features.docs && (
            <>
              <Route path="docs" element={<LazyRoute><DocsPage /></LazyRoute>} />
              <Route path="docs/new" element={<LazyRoute><DocEditorPage /></LazyRoute>} />
              <Route path="docs/:id" element={<LazyRoute><DocEditorPage /></LazyRoute>} />
              <Route path="docs/spaces/new" element={<LazyRoute><SpaceEditorPage /></LazyRoute>} />
              <Route path="docs/spaces/:id" element={<LazyRoute><SpaceEditorPage /></LazyRoute>} />
            </>
          )}

          {/* Calls module - voice agent tracking */}
          {features.calls && (
            <>
              <Route path="calls" element={<LazyRoute><CallsPage /></LazyRoute>} />
              <Route path="calls/:id" element={<LazyRoute><CallDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Email module - broadcasts and templates */}
          {features.email && (
            <>
              <Route path="email" element={<LazyRoute><EmailPage /></LazyRoute>} />
              <Route path="email/templates/new" element={<LazyRoute><TemplateEditorPage /></LazyRoute>} />
              <Route path="email/templates/:id" element={<LazyRoute><TemplateEditorPage /></LazyRoute>} />
              <Route path="email/campaigns/new" element={<LazyRoute><CampaignEditorPage /></LazyRoute>} />
              <Route path="email/campaigns/:id" element={<LazyRoute><CampaignEditorPage /></LazyRoute>} />
            </>
          )}

          {/* SMS module - two-way messaging */}
          {features.sms && (
            <Route path="sms" element={<LazyRoute><SMSConversationsPage /></LazyRoute>} />
          )}

          {/* Media library module */}
          {features.media && (
            <Route path="media" element={<LazyRoute><MediaPage /></LazyRoute>} />
          )}

          {/* File conversion module */}
          <Route path="file-conversion" element={<LazyRoute><FileConversionPage /></LazyRoute>} />

          {/* Listings module */}
          {features.listings && (
            <>
              <Route path="listings" element={<LazyRoute><ListingsPage /></LazyRoute>} />
              <Route path="listings/new" element={<LazyRoute><ListingDetailPage /></LazyRoute>} />
              <Route path="listings/:id" element={<LazyRoute><ListingDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Portfolio module */}
          {features.portfolio && (
            <>
              <Route path="portfolio" element={<LazyRoute><PortfolioPage /></LazyRoute>} />
              <Route path="portfolio/new" element={<LazyRoute><PortfolioItemDetailPage /></LazyRoute>} />
              <Route path="portfolio/:id" element={<LazyRoute><PortfolioItemDetailPage /></LazyRoute>} />
              <Route path="portfolio/collections/new" element={<LazyRoute><CollectionDetailPage /></LazyRoute>} />
              <Route path="portfolio/collections/:id" element={<LazyRoute><CollectionDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Projects module - Project management with phases and tasks */}
          {features.projects && (
            <>
              <Route path="projects" element={<LazyRoute><ProjectsPage /></LazyRoute>} />
              <Route path="projects/new" element={<LazyRoute><ProjectDetailPage /></LazyRoute>} />
              <Route path="projects/:id" element={<LazyRoute><ProjectDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Discussions module - Google Spaces-style team conversations */}
          {features.discussions && (
            <>
              <Route path="discussions" element={<LazyRoute><DiscussionsPage /></LazyRoute>} />
              <Route path="discussions/:slug" element={<LazyRoute><DiscussionSpaceDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Events module - meetings, workshops, webinars, conferences */}
          {features.events && (
            <>
              <Route path="events" element={<LazyRoute><EventsPage /></LazyRoute>} />
              <Route path="events/new" element={<LazyRoute><EventDetailPage /></LazyRoute>} />
              <Route path="events/:id" element={<LazyRoute><EventDetailPage /></LazyRoute>} />
            </>
          )}

          {/* AI Chat module - General-purpose AI assistant for all users */}
          {features.aiChat && (
            <>
              <Route path="ai-chat" element={<LazyRoute><AIChatPage /></LazyRoute>} />
              <Route path="ai-chat/:id" element={<LazyRoute><AIChatPage /></LazyRoute>} />
            </>
          )}

          {/* CMS module - visual page builder */}
          {features.cms && (
            <>
              <Route path="cms/pages" element={<LazyRoute><CmsPagesPage /></LazyRoute>} />
              <Route path="cms/pages/new" element={<LazyRoute><CmsPageEditorPage /></LazyRoute>} />
              <Route path="cms/pages/:id" element={<LazyRoute><CmsPageEditorPage /></LazyRoute>} />
              <Route path="cms/pages/:id/versions" element={<LazyRoute><CmsPageVersionsPage /></LazyRoute>} />
              <Route path="cms/l2-editor-test" element={<LazyRoute><L2EditorTestPage /></LazyRoute>} />
              <Route path="cms/menus" element={<LazyRoute><CmsMenusPage /></LazyRoute>} />
              <Route path="cms/menus/new" element={<LazyRoute><CmsMenuDetailPage /></LazyRoute>} />
              <Route path="cms/menus/:id" element={<LazyRoute><CmsMenuDetailPage /></LazyRoute>} />
            </>
          )}

          {/* Settings module - always available */}
          <Route path="settings" element={<LazyRoute><SettingsPage /></LazyRoute>} />

          {/* Activity log module - always available */}
          <Route path="activity" element={<LazyRoute><ActivityPage /></LazyRoute>} />

          {/* Reports module - analytics and reporting */}
          <Route path="reports" element={<LazyRoute><ReportsPage /></LazyRoute>} />
          <Route path="reports/pipeline" element={<LazyRoute><PipelineReportPage /></LazyRoute>} />
          <Route path="reports/activity" element={<LazyRoute><ActivityReportPage /></LazyRoute>} />
          <Route path="reports/leads" element={<LazyRoute><LeadsReportPage /></LazyRoute>} />
          <Route path="reports/cases" element={<LazyRoute><CasesReportPage /></LazyRoute>} />

          {/* Profile redirects to Settings (Profile tab is default) */}
          <Route path="profile" element={<Navigate to="/dashboard/settings" replace />} />

          {/* Admin module - consolidated settings page */}
          <Route path="admin/settings" element={<LazyRoute><AdminSettingsPage /></LazyRoute>} />
          <Route path="admin/system-prompts/new" element={<LazyRoute><SystemPromptDetailPage /></LazyRoute>} />
          <Route path="admin/system-prompts/:id" element={<LazyRoute><SystemPromptDetailPage /></LazyRoute>} />

          {/* Development tools */}
          {features.styleGuide && (
            <Route path="style-guide" element={<LazyRoute><StyleGuidePage /></LazyRoute>} />
          )}
        </Route>

        {/* Public documentation pages (no auth required) */}
        {features.docs && (
          <>
            <Route path="/docs" element={<LazyFullPageRoute><PublicDocsIndexPage /></LazyFullPageRoute>} />
            <Route path="/docs/*" element={<LazyFullPageRoute><PublicDocPage /></LazyFullPageRoute>} />
          </>
        )}

        {/* Public shared AI chat page (no auth required) */}
        <Route path="/shared/chat/:token" element={<LazyFullPageRoute><SharedChatPage /></LazyFullPageRoute>} />

        {/* Public quote view page (no auth required) */}
        <Route path="/quote/:token" element={<LazyFullPageRoute><PublicQuotePage /></LazyFullPageRoute>} />

        {/* Public blog pages (no auth required) */}
        {features.cms && (
          <>
            <Route path="/blog" element={<LazyFullPageRoute><BlogIndexPage /></LazyFullPageRoute>} />
            <Route path="/blog/category/:slug" element={<LazyFullPageRoute><BlogCategoryPage /></LazyFullPageRoute>} />
            <Route path="/blog/tag/:slug" element={<LazyFullPageRoute><BlogTagPage /></LazyFullPageRoute>} />
          </>
        )}

        {/* Public CMS pages (no auth required) */}
        {features.cms && (
          <Route path="/p/*" element={<LazyFullPageRoute><CmsPublicPage /></LazyFullPageRoute>} />
        )}

        {/* Catch all - redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
